import { PriceUtils } from '@wix/bookings-uou-mappers';
import { useTranslation } from '@wix/yoshi-flow-editor';
import React from 'react';
import { FormNestedSlot } from '../../../../../types/formSelectedSlots';
import { SlotService } from '../../../../../utils/state/types';
import { getParticipantHeading } from '../../../../../utils/multiParticipantFlow';
import {
  getParticipantPayment,
  isPlanPayment,
} from '../../../../../utils/multiParticipantFlow/participantPayment';
import { getParticipantName } from '../../../../../utils/multiParticipantFlow/participants';
import { TagName } from '../../../../../utils/tagName.const';
import Text, { TextType } from '../../../Text/Text';
import { useFormComponentContext } from '../../../Hooks/useFormComponentContext';
import Duration from '../../Duration/Duration';
import Location from '../../Location/Location';
import StaffMembers from '../../StaffMembers/StaffMembers';
import { SelectedAddons } from './SelectedAddons';
import { SingleSlotDetailsDataHook } from './dataHooks';
import { classes } from './ParticipantDetails.st.css';

export type ParticipantDetailsProps = {
  slotService: SlotService;
  slot: FormNestedSlot;
  participantIndex: number;
};

/**
 * Booking Details, multi-participant flow: one participant's booking —
 * "Participant N - name", the service with the chosen variant and its price (or, on its
 * own line, the plan it is paid with),
 * location / staff / duration, and the participant's add-ons.
 */
export const ParticipantDetails: React.FC<ParticipantDetailsProps> = ({
  slotService,
  slot,
  participantIndex,
}) => {
  const { t } = useTranslation();
  const { participantFormValues, dateRegionalSettingsLocale } =
    useFormComponentContext();
  const participant = slotService.participants?.[participantIndex];
  if (!participant) {
    return null;
  }

  const { service, resourceSelections } = slotService;
  const heading = getParticipantHeading(
    t,
    participantIndex + 1,
    getParticipantName(participantFormValues?.[participantIndex]),
  );
  const variantName = participant.selectedVariant?.choices?.[0]?.custom;
  const payment = getParticipantPayment(slotService, participantIndex);
  const planName = isPlanPayment(payment)
    ? payment.selectedPaymentOption.label
    : undefined;
  const withPrice = (name: string) =>
    planName
      ? name
      : `${name} (${PriceUtils.getFormattedCurrency({
          ...getParticipantPrice(slotService, variantName),
          locale: dateRegionalSettingsLocale,
        })})`;

  return (
    <div
      className={classes.root}
      data-hook={`${SingleSlotDetailsDataHook.PARTICIPANT_DETAILS}-${participantIndex}`}
    >
      <Text
        type={TextType.Primary}
        tagName={TagName.Paragraph}
        data-hook={SingleSlotDetailsDataHook.PARTICIPANT_NAME}
      >
        {heading}
      </Text>
      <div className={classes.details}>
        <Text
          type={TextType.Secondary}
          tagName={TagName.Paragraph}
          className={classes.serviceName}
          data-hook={SingleSlotDetailsDataHook.SERVICE_NAME}
        >
          {variantName ? service.name : withPrice(service.name)}
        </Text>
        {variantName && (
          <Text
            type={TextType.Secondary}
            tagName={TagName.Paragraph}
            data-hook={SingleSlotDetailsDataHook.PARTICIPANT_VARIANT}
          >
            {withPrice(variantName)}
          </Text>
        )}
        {planName && (
          <Text
            type={TextType.Secondary}
            tagName={TagName.Paragraph}
            data-hook={SingleSlotDetailsDataHook.PARTICIPANT_PLAN}
          >
            {planName}
          </Text>
        )}
        <Location location={service.location} />
        <StaffMembers
          service={service}
          resourceSelections={resourceSelections}
        />
        <Duration
          slotService={slotService}
          slot={slot}
          totalNumberOfSessions={service.totalNumberOfSessions}
          remainingNumberOfSessions={service.remainingNumberOfSessions}
        />
        <SelectedAddons
          slotService={slotService}
          selectedAddons={participant.selectedAddons}
          dataHook={`${SingleSlotDetailsDataHook.PARTICIPANT_ADDONS}-${participantIndex}`}
        />
      </div>
    </div>
  );
};

/**
 * The participant's session price: the chosen variant's price, or the service's per-participant
 * price. Not `paymentDetails.price` — the calculator has already multiplied that by the
 * number of participants.
 */
const getParticipantPrice = (
  { service, paymentDetails, dynamicPriceInfo }: SlotService,
  variantName: string | undefined,
) => {
  const variantPrice = dynamicPriceInfo?.paymentDetails?.find(
    ({ choice }) => choice === variantName,
  )?.price;
  const rawPrice = variantPrice?.value?.trim();
  const price = Number(rawPrice);
  return rawPrice && Number.isFinite(price)
    ? {
        price,
        currency: variantPrice!.currency || paymentDetails.currency,
      }
    : {
        price: service.payment.paymentDetails.price,
        currency: paymentDetails.currency,
      };
};

export default ParticipantDetails;
